if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
          ({ dataset }) => dataset.optionValueId
        );
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('.product-form__input'));
      }

      get variantData() {
        if (this._variantData) return this._variantData;
        const el = this.querySelector('[data-variant-data]');
        this._variantData = el ? JSON.parse(el.textContent) : [];
        return this._variantData;
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        if (this._hasChangeListener) {
          return;
        }

        this._hasChangeListener = true;

        this.addEventListener(
          'change',
          (event) => {
            const targetInThisElement = this.contains(event.target);

            let hasNestedVariantSelects = false;
            let currentElement = event.target;

            while (currentElement && currentElement !== document.body) {
              if (currentElement.tagName === 'VARIANT-SELECTS') {
                if (currentElement !== this) {
                  hasNestedVariantSelects = true;
                  break;
                } else {
                  break;
                }
              }
              currentElement = currentElement.parentElement;
            }

            if (!targetInThisElement || hasNestedVariantSelects) {
              return;
            }

            const target = this.getInputForEventTarget(event.target);
            this.updateSelectedSwatchValue(event);

            // Correct the selection to the nearest available variant BEFORE publishing,
            // so product-info fetches the corrected combination in a single request.
            this.resolveAvailableSelection(target);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event,
                target,
                selectedOptionValues: this.selectedOptionValues,
              },
            });
          },
          true
        ); // Use CAPTURE phase to catch events before they bubble
      }

      /**
       * Reads the option values currently checked/selected in the DOM,
       * in option order (index 0 = option1, 1 = option2, ...).
       */
      currentOptionValues() {
        return this.optionGroups.map((group) => {
          const input = group.querySelector('select option[selected], input:checked');
          return input ? input.value : null;
        });
      }

      /**
       * If the current combination is missing or sold out, find the closest
       * available variant that keeps the option the user just changed, and
       * apply it to the inputs. Options AFTER the changed one are relaxed first,
       * then options before it.
       */
      resolveAvailableSelection(target) {
        const variants = this.variantData;
        if (!variants.length) return;

        const keys = ['option1', 'option2', 'option3'];
        const changedGroup = target.closest('.product-form__input');
        const changedIndex = this.optionGroups.indexOf(changedGroup);
        if (changedIndex === -1) return;

        const values = this.currentOptionValues();

        const matches = (variant, constraints) =>
          constraints.every((value, i) => value === null || variant[keys[i]] === value);

        const exact = variants.find((variant) => matches(variant, values));
        if (exact && exact.available) return; // Nothing to fix.

        // Relax later options first, then earlier ones. Never relax the changed option.
        const relaxOrder = [];
        for (let i = values.length - 1; i > changedIndex; i--) relaxOrder.push(i);
        for (let i = changedIndex - 1; i >= 0; i--) relaxOrder.push(i);

        const constraints = [...values];
        for (const index of relaxOrder) {
          constraints[index] = null;
          const candidate = variants.find((variant) => variant.available && matches(variant, constraints));
          if (candidate) {
            this.applyVariant(candidate);
            return;
          }
        }
        // No available variant keeps the changed option — leave it, product-info will
        // fall through to setUnavailable().
      }

      applyVariant(variant) {
        const keys = ['option1', 'option2', 'option3'];

        this.optionGroups.forEach((group, i) => {
          const value = variant[keys[i]];
          if (!value) return;

          const select = group.querySelector('select');
          const label = group.querySelector('[data-selected-swatch-value]');

          if (select) {
            Array.from(select.options).forEach((option) => {
              const isMatch = option.value === value;
              option.selected = isMatch;
              if (isMatch) {
                option.setAttribute('selected', 'selected');
              } else {
                option.removeAttribute('selected');
              }
            });
          } else {
            const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
              (radio) => radio.value === value
            );
            if (!input || input.checked) return;
            input.checked = true;
          }

          if (label) label.textContent = value;
        });
      }

      // NEW: given a media/image id (from a gallery thumbnail or main slide click),
      // find the variant that owns it and select it — reusing applyVariant() and
      // publishing through the same pubsub event the existing change handler uses,
      // so product-info.js updates price/URL/SKU/add-to-cart exactly as it already does.
      selectVariantByMediaId(mediaId) {
        const variant = this.variantData.find(
          (v) => String(v.featured_media?.id) === String(mediaId) || String(v.featured_image?.id) === String(mediaId)
        );

        // No variant owns this image, or it's sold out — leave the picker alone,
        // just let the gallery show the photo.
        if (!variant || !variant.available) return;

        this.applyVariant(variant);

        const target = this.optionGroups[0]?.querySelector('select, input:checked, input[type="radio"]');
        if (!target) return;

        this.updateSelectedSwatchValue({ target });

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event: { target },
            target,
            selectedOptionValues: this.selectedOptionValues,
          },
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            .removeAttribute('selected');
          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }
    }
  );
}